import ko from "knockout";
import Bluebird from "bluebird";
import i18n from "extensions/i18n/knockout.i18n";
import loggingService from "services/loggingService";
import englishStrings from "strings/en-US.json";

const localePromises = {};
const initialized = ko.observable(false);

function setupLocale(locale, strings) {
    i18n.registerStrings(strings, locale);

    initialized(true);
}

/**
 * Checks to see if the current application locale is set to the passed locale, and
 * if not, reloads the page.
 */
function checkAppLocale(desiredLocale) {
    const appLocale = ko.i18n.getLocale();
    if (desiredLocale && appLocale && desiredLocale !== appLocale) {
        loggingService.error("Reloading due to incorrect locale", {
            desiredLocale: desiredLocale,
            appLocale: appLocale,
        });
        /*
         As far as we know, we don't want to do a reload here;
         but we might needs to bring this back in the future;
        */
        // window.location.reload();
    }
}

/**
 * Module for requesting that locale data be loaded into the application.
 * @exports services/localeService
 */
const localeService = {
    initialized: ko.pureComputed(function () {
        return initialized();
    }),

    /**
     * Gets the current locale the application should be in, based on user preferences, browser config, and default settings.
     */
    getAppLocale: function () {
        return ko.i18n.getLocale();
    },

    /**
     * Requests that the provided locale data be loaded into the application.
     *
     * After loading, a locale's localized string registry will be available
     * to the application and number/date/currency formatting will work for
     * that locale via the {@link i18n} module.
     *
     * @param {String} locale the locale to load.
     * @returns {Bluebird<string>} a promise object that will be resolved when the locale
     *         files have been loaded
     */
    loadLocale: function (locale) {
        // make sure the country code is captilized to match our translation file names
        locale = locale
            .split("-")
            .map((curr, ind, coll) => (ind === 1 ? curr.toUpperCase() : curr))
            .join("-");
        if (locale === "en-US") {
            setupLocale(locale, englishStrings);
            return Bluebird.resolve(locale);
        }

        const localePromise =
            localePromises[locale] ||
            new Bluebird(function (resolve) {
                // stringsContext is a special `require` function for getting the locale strings
                function _loadLocale(stringsContext) {
                    let localeStrings;
                    try {
                        localeStrings = stringsContext(`./${locale}.json`);
                    } catch (e) {
                        loggingService.warn(
                            `Failed to load locale ${locale}, falling back to en-US`,
                            e
                        );
                        locale = "en-US";
                        localeStrings = englishStrings;
                    }
                    setupLocale(locale, localeStrings);
                    resolve(locale);
                }

                /*
                 * This pattern splits up the bundle based on the first letter of the locale;
                 * splitting the i18n chunk will reduce the amount that needs to be loaded for any one language.
                 * The redundancy of this code is mostly necessary for the sake of the webpack compiler
                 */
                if (/^[a-g]/i.test(locale)) {
                    require.ensure(
                        [],
                        (require) => {
                            const context = require.context(
                                "strings/",
                                false,
                                /^\.\/[a-g].+\.json/i
                            );
                            _loadLocale(context);
                        },
                        "i18nChunkA"
                    );
                } else if (/^[h-o]/i.test(locale)) {
                    require.ensure(
                        [],
                        (require) => {
                            const context = require.context(
                                "strings/",
                                false,
                                /^\.\/[h-o].+\.json/i
                            );
                            _loadLocale(context);
                        },
                        "i18nChunkB"
                    );
                } else {
                    require.ensure(
                        [],
                        (require) => {
                            const context = require.context(
                                "strings/",
                                false,
                                /^\.\/[p-z].+\.json/i
                            );
                            _loadLocale(context);
                        },
                        "i18nChunkC"
                    );
                }
            });
        localePromises[locale] = localePromise;

        return localePromise;
    },

    setLocale: (locale) => {
        ko.i18n.setLocale(locale);
        localeService.onLocaleChanged.notifySubscribers();
    },

    onLocaleChanged: ko.observable(),
};

checkAppLocale(localeService.getAppLocale());

export default localeService;
